/**
 * Sentry Frontend Initialization
 *
 * Configures error monitoring, performance tracing, and session replay
 * for production visibility into user-facing errors.
 */

import * as Sentry from '@sentry/react';

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  // Skip initialization if no DSN configured
  if (!dsn) {
    if (import.meta.env.DEV) {
      console.warn('[Sentry] Skipping initialization - VITE_SENTRY_DSN not configured');
    }
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development',
    release: import.meta.env.VITE_SENTRY_RELEASE,

    // Enable Sentry Logs feature - sends logs to Sentry Logs section
    _experiments: {
      enableLogs: true,
    },

    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration(),
      // Session replay for debugging user sessions
      Sentry.replayIntegration({
        maskAllText: true, // Privacy: mask all text content
        blockAllMedia: true, // Privacy: block media elements
      }),
    ],

    // Performance monitoring: sample 10% of transactions
    tracesSampleRate: 0.1,

    // Session replay sampling
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% when error occurs

    // Filter events before sending
    beforeSend(event) {
      // In dev: log event but still send for testing (set VITE_SENTRY_SEND_IN_DEV=true to enable)
      if (import.meta.env.DEV) {
        console.log('[Sentry] Event captured:', event);
        // Only send in dev if explicitly enabled
        if (!import.meta.env.VITE_SENTRY_SEND_IN_DEV) {
          return null;
        }
      }

      // Scrub sensitive data from URLs
      if (event.request?.url) {
        event.request.url = scrubSensitiveParams(event.request.url);
      }

      return event;
    },

    // Filter breadcrumbs
    beforeBreadcrumb(breadcrumb) {
      // Console logs are now captured via consoleLoggingIntegration to Sentry Logs
      // Keep console breadcrumbs for additional context in error traces
      return breadcrumb;
    },
  });
}

/**
 * Scrub sensitive query parameters from URLs
 */
function scrubSensitiveParams(url: string): string {
  try {
    const urlObj = new URL(url);
    const sensitiveParams = ['token', 'access_token', 'refresh_token', 'api_key', 'key'];

    sensitiveParams.forEach((param) => {
      if (urlObj.searchParams.has(param)) {
        urlObj.searchParams.set(param, '[REDACTED]');
      }
    });

    return urlObj.toString();
  } catch {
    return url;
  }
}
